import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RelationshipV2 } from 'support/types';
import { ConnectionSetupTableRow } from 'components/ConnectionSetupTable/ConnectionSetupTable';
import { StepStatus } from 'components/Display/Steps/Steps';

type Params = {
  stepStatus: StepStatus;
  relationship: RelationshipV2 | undefined;
  isSender: boolean;
  routeTo: (subPath: 'testRelationship', params: Record<string, string>) => string;
  partnerId: string;
};

export const useTestingStep = ({ stepStatus, relationship, isSender, routeTo, partnerId }: Params) => {
  const { t } = useTranslation();

  const isReadyForTesting = relationship?.status === 'READY_FOR_TESTING';
  const hasTestMessagesOrInReview =
    relationship?.status === 'HAS_TEST_MESSAGES' || relationship?.status === 'IN_REVIEW';

  const rows: Array<ConnectionSetupTableRow> = useMemo(
    () => [
      {
        id: 'send-test-documents',
        status: stepStatus === 'completed' || hasTestMessagesOrInReview ? 'DONE' : 'TODO',
        title: t('connections:relationshipDetails.steps.testing.tasks.sendTestDocuments'),
        ownership: isSender ? 'you' : 'trade_partner',
        actionRequired: isSender && stepStatus === 'current' && isReadyForTesting,
        path:
          isSender && relationship
            ? routeTo('testRelationship', { partnerId, relationshipId: relationship.id })
            : undefined,
      },
      {
        id: 'review-test-documents',
        status: stepStatus === 'completed' ? 'DONE' : 'TODO',
        title: t('connections:relationshipDetails.steps.testing.tasks.reviewTestDocuments'),
        ownership: isSender ? 'trade_partner' : 'you',
        actionRequired: !isSender && stepStatus === 'current' && hasTestMessagesOrInReview,
        path:
          !isSender && relationship
            ? routeTo('testRelationship', { partnerId, relationshipId: relationship.id })
            : undefined,
      },
    ],
    [stepStatus, relationship, isSender, routeTo, partnerId, t, isReadyForTesting, hasTestMessagesOrInReview],
  );

  return { rows };
};
